import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  DerinOrganization,
  MigrationPreview,
  MigrationResult,
  getMigrationPreview,
  getOrganizations,
  runMigration,
} from "../../../api/derin";
import { useToast } from "../Toast";
import { errorMessageOf } from "../errorMessage";
import ConfirmMigrationDialog from "./ConfirmMigrationDialog";
import FixUsersCard from "./FixUsersCard";
import {
  chipSx,
  collectionColors,
  font,
  subtitleSx,
} from "../CollectionsTab/tokens";

/**
 * Surveys → collections backfill.
 *
 * Surveys uploaded before collections existed have none, which makes them invisible in
 * the Collections tab. Since no survey has language pairs yet, each one becomes a
 * collection named after it, holding that survey as its single primary variant.
 *
 * Admin-only and rendered by AdminControlsPage, which owns the check. Also holds
 * "Fix duplicate users" (FixUsersCard), the merge of pre-membership +alias accounts.
 */
const MigrationSection: React.FC = () => {
  const { showSuccess, showError } = useToast();

  const [organizations, setOrganizations] = useState<DerinOrganization[]>([]);
  const [organizationId, setOrganizationId] = useState("");
  const [preview, setPreview] = useState<MigrationPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<MigrationResult | null>(null);

  const fetchOrganizations = useCallback(async () => {
    try {
      setOrganizations(await getOrganizations());
    } catch (err) {
      console.error("Error fetching organizations:", err);
      showError(`Error fetching organizations: ${errorMessageOf(err)}`);
    }
  }, [showError]);

  useEffect(() => {
    void fetchOrganizations();
  }, [fetchOrganizations]);

  const loadPreview = useCallback(
    async (id: string) => {
      setIsLoadingPreview(true);
      try {
        setPreview(await getMigrationPreview(id));
      } catch (err) {
        console.error("Error loading migration preview:", err);
        showError(`Error loading migration preview: ${errorMessageOf(err)}`);
        setPreview(null);
      } finally {
        setIsLoadingPreview(false);
      }
    },
    [showError]
  );

  const handleSelectOrganization = (id: string) => {
    setOrganizationId(id);
    setResult(null);
    setPreview(null);
    if (id) {
      void loadPreview(id);
    }
  };

  const handleExecute = async () => {
    setConfirmOpen(false);
    setIsMigrating(true);
    try {
      const migrationResult = await runMigration(organizationId);
      setResult(migrationResult);
      showSuccess(
        `${migrationResult.migrated.length} survey${
          migrationResult.migrated.length === 1 ? "" : "s"
        } migrated to collections`
      );
      // Refresh so the remaining candidates reflect what actually happened.
      await loadPreview(organizationId);
    } catch (err) {
      console.error("Error running migration:", err);
      showError(`Error running migration: ${errorMessageOf(err)}`);
    } finally {
      setIsMigrating(false);
    }
  };

  const candidates = preview?.candidates ?? [];
  const selectedOrganization = organizations.find(
    (organization) => organization.id === organizationId
  );

  /**
   * Names that appear more than once. Dev has two organizations both called "Test
   * Organization", which is enough to migrate one and then verify the other — so those
   * entries get their creation date to tell them apart.
   */
  const duplicatedNames = new Set(
    organizations
      .map((organization) => organization.name)
      .filter((name, index, names) => names.indexOf(name) !== index)
  );

  const organizationLabel = (organization: DerinOrganization): string =>
    duplicatedNames.has(organization.name)
      ? `${organization.name} · created ${new Date(organization.dateCreated).toLocaleDateString()}`
      : organization.name;

  return (
    <Box sx={{ padding: "0px 30px 30px", display: "flex", flexDirection: "column", gap: "20px" }}>
      <Box
        sx={{
          boxSizing: "border-box",
          width: "100%",
          background: collectionColors.white,
          border: `1px solid ${collectionColors.border}`,
          borderRadius: "14px",
          padding: "25px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LayersOutlinedIcon
              sx={{ width: 18, height: 18, color: collectionColors.textSecondary }}
            />
            <Typography
              sx={{
                fontFamily: font,
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "27px",
                color: collectionColors.textPrimary,
              }}
            >
              Migrate surveys to collections
            </Typography>
          </Box>
          <Typography sx={{ ...subtitleSx, fontSize: "16px", lineHeight: "24px" }}>
            Surveys created before collections existed have no collection, so they do not
            show up in the Collections tab. Each one becomes a collection named after the
            survey, with the survey as its only variant. Running this twice is safe.
          </Typography>
        </Box>

        {/* Organization picker */}
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{
                fontFamily: font,
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "14px",
                color: collectionColors.textPrimary,
              }}
            >
              Organization
            </Typography>
            <FormControl>
              <Select
                displayEmpty
                value={organizationId}
                onChange={(event) => handleSelectOrganization(event.target.value)}
                disabled={isMigrating}
                sx={{
                  width: "320px",
                  height: "36px",
                  background: collectionColors.surfaceLight,
                  borderRadius: "8px",
                  fontFamily: font,
                  fontSize: "14px",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
                renderValue={(value) =>
                  value ? (
                    selectedOrganization ? (
                      organizationLabel(selectedOrganization)
                    ) : (
                      value
                    )
                  ) : (
                    <Typography sx={{ ...subtitleSx, fontSize: "14px" }}>
                      Select an organization
                    </Typography>
                  )
                }
              >
                {organizations.map((organization) => (
                  <MenuItem
                    key={organization.id}
                    value={organization.id}
                    sx={{ fontFamily: font, fontSize: "14px" }}
                  >
                    {organizationLabel(organization)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={
              !organizationId || isLoadingPreview || isMigrating || candidates.length === 0
            }
            startIcon={
              isMigrating ? (
                <CircularProgress size={14} sx={{ color: collectionColors.white }} />
              ) : undefined
            }
            sx={{
              height: "36px",
              padding: "6px 16px",
              borderRadius: "8px",
              background: collectionColors.buttonPrimary,
              color: collectionColors.white,
              fontFamily: font,
              fontWeight: 500,
              fontSize: "14px",
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": { background: collectionColors.buttonPrimaryHover },
              "&.Mui-disabled": {
                background: collectionColors.buttonPrimary,
                opacity: 0.4,
                color: collectionColors.white,
              },
            }}
          >
            {isMigrating ? "Migrating…" : "Execute migration"}
          </Button>
        </Box>

        {/* Preview */}
        {isLoadingPreview ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !organizationId ? (
          <Typography sx={subtitleSx}>
            Select an organization to see what would be migrated.
          </Typography>
        ) : preview ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Box sx={chipSx}>{preview.totalSurveys} surveys</Box>
              <Box sx={chipSx}>{preview.alreadyMigrated} already in a collection</Box>
              <Box
                sx={{
                  ...chipSx,
                  ...(candidates.length > 0
                    ? {
                        background: collectionColors.buttonAccent,
                        border: "1px solid transparent",
                      }
                    : {}),
                }}
              >
                {candidates.length} to migrate
              </Box>
            </Box>

            {candidates.length === 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <CheckCircleOutlineIcon
                  sx={{ width: 18, height: 18, color: "#16A34A" }}
                />
                <Typography sx={subtitleSx}>
                  Nothing to migrate — every survey in this organization already belongs to
                  a collection.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Survey", "Survey ID", "Variant label", "Versions", "Responses"].map(
                        (header) => (
                          <TableCell
                            key={header}
                            sx={{
                              fontFamily: font,
                              fontWeight: 500,
                              fontSize: "13px",
                              color: collectionColors.textSecondary,
                              borderBottom: `1px solid ${collectionColors.border}`,
                            }}
                          >
                            {header}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidates.map((candidate) => (
                      <TableRow key={candidate.surveyOriginalId}>
                        <TableCell
                          sx={{
                            fontFamily: font,
                            fontSize: "14px",
                            color: collectionColors.textPrimary,
                          }}
                        >
                          {candidate.title}
                        </TableCell>
                        <TableCell sx={{ ...subtitleSx, fontSize: "13px" }}>
                          {candidate.surveyOriginalId}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ ...chipSx, display: "inline-flex" }}>
                            {candidate.variantLabel}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...subtitleSx, fontSize: "13px" }}>
                          {candidate.versionCount}
                        </TableCell>
                        <TableCell sx={{ ...subtitleSx, fontSize: "13px" }}>
                          {candidate.transcriptCount}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : null}

        {/* Result */}
        {result && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "16px",
              background: collectionColors.surfaceLight,
              borderRadius: "10px",
            }}
          >
            <Typography
              sx={{
                fontFamily: font,
                fontWeight: 500,
                fontSize: "14px",
                color: collectionColors.textPrimary,
              }}
            >
              Last run: {result.migrated.length} migrated · {result.skipped} already in a
              collection
            </Typography>
            {result.migrated.slice(0, 5).map((migrated) => (
              <Typography key={migrated.surveyOriginalId} sx={{ ...subtitleSx, fontSize: "13px" }}>
                ✓ {migrated.collectionName} — variant “{migrated.variantLabel}”
              </Typography>
            ))}
            {result.migrated.length > 5 && (
              <Typography sx={{ ...subtitleSx, fontSize: "13px" }}>
                …and {result.migrated.length - 5} more
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <ConfirmMigrationDialog
        open={confirmOpen}
        organizationName={selectedOrganization?.name ?? ""}
        candidateCount={candidates.length}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleExecute}
      />

      <FixUsersCard />
    </Box>
  );
};

export default MigrationSection;
